import { useEffect, useState } from "react";
import { Users, PhoneCall, PhoneOutgoing, Trophy, Repeat, Zap } from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/stages";
import Funnel from "../components/Funnel";
import RevenueCalculator from "../components/RevenueCalculator";

const MetricCard = ({ icon: Icon, label, value, sub, accent, testId, delay }) => (
  <div className={`glass-panel glass-hover p-5 fade-up`} style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <span className="eyebrow text-[10px]">{label}</span>
      <Icon size={16} className={accent} />
    </div>
    <p className="metric-value">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data));
  }, []);

  if (!stats) {
    return <p className="eyebrow">Loading metrics…</p>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="fade-up">
        <p className="eyebrow mb-1">Command Center</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Pipeline Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard icon={Users} label="Total Leads" value={stats.total_leads} accent="text-sky-400" testId="metric-total-leads" delay={0} />
        <MetricCard icon={PhoneCall} label="Calls Scheduled" value={stats.calls_scheduled} sub={`${stats.rates.lead_to_call}% of leads`} accent="text-blue-400" testId="metric-calls-scheduled" delay={50} />
        <MetricCard icon={PhoneOutgoing} label="Calls Completed" value={stats.calls_completed} sub={`${stats.rates.call_to_completed}% of scheduled`} accent="text-[#4690d9]" testId="metric-calls-completed" delay={100} />
        <MetricCard icon={Trophy} label="Closed Won" value={stats.closed_won} sub={`${stats.rates.lead_to_won}% of leads`} accent="text-emerald-400" testId="metric-closed-won" delay={150} />
        <MetricCard icon={Repeat} label="Closed MRR" value={fmtMoney(stats.mrr_closed)} sub="monthly recurring" accent="text-emerald-400" testId="metric-mrr" delay={200} />
        <MetricCard icon={Zap} label="Self-Booked" value={stats.self_scheduled} sub="booked without outreach" accent="text-amber-400" testId="metric-self-booked" delay={250} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Funnel stats={stats} />
        <RevenueCalculator stats={stats} />
      </div>

      <div className="glass-panel p-5 flex flex-wrap items-center gap-x-8 gap-y-2 fade-up" data-testid="pipeline-summary-bar">
        <span className="eyebrow text-[10px]">Revenue Status</span>
        <span className="text-sm text-slate-300">
          Closed MRR: <span className="font-mono-tech text-emerald-300 font-bold">{fmtMoney(stats.mrr_closed)}/mo</span>
        </span>
        <span className="text-sm text-slate-300">
          Setup fees closed: <span className="font-mono-tech text-white font-bold">{fmtMoney(stats.setup_closed)}</span>
        </span>
        <span className="text-sm text-slate-300">
          Open MRR pipeline: <span className="font-mono-tech text-[#9cc6f0] font-bold">{fmtMoney(stats.mrr_pipeline)}/mo</span>
        </span>
        <span className="text-sm text-slate-300">
          Needs outreach: <span className="font-mono-tech text-amber-300 font-bold">{stats.needs_outreach}</span>
        </span>
        <span className="text-sm text-slate-300">
          Follow-ups: <span className="font-mono-tech text-amber-300 font-bold">{stats.follow_up}</span>
        </span>
        <span className="text-sm text-slate-300">
          Lost: <span className="font-mono-tech text-rose-300 font-bold">{stats.closed_lost}</span>
        </span>
      </div>
    </div>
  );
}
